(() => {
  if (customElements.get('product-card-specifications')) return;
  const requests = new Map();
  const excluded = new Set(['skin tone', 'skin tone*', 'color', 'colour', 'tone', '肤色', '颜色']);
  const valid = (value) => value && value !== '-' && value !== '—';
  const readTable = (html) => {
    const document = new DOMParser().parseFromString(html, 'text/html');
    const table = document.querySelector('[data-product-specifications] table');
    if (!table) return [];
    const columns = Array.from(table.querySelectorAll('thead tr:first-child th')).slice(1).map((cell) => cell.textContent.trim());
    return Array.from(table.querySelectorAll('tbody tr')).map((row) => ({
      label: row.querySelector('th')?.textContent.trim() || '',
      values: Array.from(row.querySelectorAll('td')).map((cell, index) => ({ text: cell.textContent.trim(), column: columns.length > 1 ? columns[index] || '' : '' })).filter((value) => valid(value.text))
    })).filter((row) => row.label && !excluded.has(row.label.toLowerCase()) && row.values.length);
  };
  const load = (url) => {
    if (!requests.has(url)) {
      requests.set(url, fetch(url, { credentials: 'same-origin' }).then((response) => {
        if (!response.ok) throw new Error('Unavailable product page');
        return response.text();
      }).then(readTable).catch(() => []));
    }
    return requests.get(url);
  };
  class ProductCardSpecifications extends HTMLElement {
    connectedCallback() {
      if (this.loaded || this.loading || this.observer) return;
      if (!('IntersectionObserver' in window)) {
        this.showMeasurements();
        return;
      }
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.observer?.disconnect();
          this.observer = null;
          this.showMeasurements();
        }
      }, { rootMargin: '300px' });
      this.observer.observe(this.parentElement || this);
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;
    }
    async showMeasurements() {
      if (this.loaded || this.loading) return;
      const path = this.dataset.productUrl;
      if (!path) return;
      let url;
      try {
        url = new URL(path, window.location.origin);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      const localeRoot = window.Shopify?.routes?.root;
      if (localeRoot && url.pathname.startsWith('/products/')) {
        url.pathname = localeRoot.replace(/\/$/, '') + url.pathname;
      }
      url.hash = '';
      this.loading = true;
      const rows = await load(url.href);
      this.loading = false;
      if (!this.isConnected) return;
      this.loaded = true;
      const selected = new Set((this.dataset.rowNames || '').split(/\r?\n/).map((name) => name.trim().toLowerCase()).filter(Boolean));
      const limit = Math.min(12, Math.max(1, Number.parseInt(this.dataset.limit, 10) || 4));
      const measurements = rows.filter((row) => !selected.size || selected.has(row.label.toLowerCase())).slice(0, limit);
      if (!measurements.length) return;
      const list = document.createElement('dl');
      list.className = 'product-card-specifications';
      for (const measurement of measurements) {
        const row = document.createElement('div');
        row.className = 'product-card-specifications__row';
        const label = document.createElement('dt');
        label.textContent = measurement.label;
        const values = document.createElement('dd');
        for (const value of measurement.values) {
          const content = document.createElement('span');
          content.className = 'product-card-specifications__value';
          if (value.column) {
            const column = document.createElement('span');
            column.className = 'product-card-specifications__column';
            column.textContent = value.column;
            content.append(column);
          }
          content.append(document.createTextNode(value.text));
          values.append(content);
        }
        row.append(label, values);
        list.append(row);
      }
      this.replaceChildren(list);
      this.hidden = false;
    }
  }
  customElements.define('product-card-specifications', ProductCardSpecifications);
})();
